import { Cart, CartItem } from "../../contexts/cart/types";
import { useEffect, useState } from "react";
import * as React from "react";
import { RequestParams, useRequest } from "../use-request";

import { CustomerGroup } from "../../contexts/auth/state";
import useAuth from "../use-auth";

const API_URL = `/api/get-special-rate`;

type DiscountMap = {
  [concatString: string]: {
    discount: number;
    [key: string]: any;
  };
};
type DiscountMapResponse = { data: DiscountMap };

export interface StaffDiscountInterface {
  cartItemsRegularPrice: DiscountMap;
  totalStaffDiscount: number;
  fetchTotalStaffDiscount: React.Dispatch<Array<CartItem>>;
  isLoading: boolean;
  error?: string;
}

interface Params {
  lineItems?: Array<CartItem>;
  customerGroup?: CustomerGroup;
}

export const useStaffDiscount = (cart: Cart): StaffDiscountInterface => {
  const [cartItemsRegularPrice, setCartItemsRegularPrice] = useState({});
  const [totalStaffDiscount, setTotalStaffDiscount] = useState(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>(undefined);
  const { user } = useAuth();
  const customerGroup = user?.customer_group || {};
  const [requestParams, setRequestParams] = useState<RequestParams>(
    createRequestParams({ lineItems: cart?.lineItems, customerGroup })
  );

  const staffDiscountRequest = useRequest(requestParams);

  useEffect(() => {
    if (cart && staffDiscountRequest.data && staffDiscountRequest.isSuccess) {
      const { data: discountMap } = staffDiscountRequest.data;
      const totalDiscount = cart?.lineItems?.reduce((acc, item) => {
        const totalRegularPrice_ =
          discountMap[item.productId + "-" + item.name]?.packPrice *
          item.quantity;
        discountMap[item.productId + "-" + item.name] = {
          ...discountMap[item.productId + "-" + item.name],
          totalRegularPrice: Number.isNaN(totalRegularPrice_)
            ? item.salePrice
            : totalRegularPrice_,
        };

        const returnValue =
          acc +
          (discountMap[item.productId + "-" + item.name]?.discount || 0) *
            item.quantity;

        return Number.isNaN(returnValue) ? 0 : returnValue;
      }, 0);

      setCartItemsRegularPrice(discountMap);
      setTotalStaffDiscount(totalDiscount);
    }
  }, [staffDiscountRequest.data, cart?.lineItems, customerGroup]);

  useEffect(() => {
    setIsLoading(staffDiscountRequest.isLoading && !staffDiscountRequest.error);
  }, [staffDiscountRequest.isLoading, staffDiscountRequest.error]);

  useEffect(() => {
    setError("Failed to fetch order history");
  }, [staffDiscountRequest.error]);

  return {
    cartItemsRegularPrice,
    totalStaffDiscount,
    isLoading,
    error,
    fetchTotalStaffDiscount: (lineItems: Array<CartItem>) => {
      setRequestParams(createRequestParams({ lineItems, customerGroup }));
    },
  };
};

const createRequestParams = ({
  lineItems = [],
  customerGroup,
}: Params): RequestParams => {
  const bigCommerceIds = lineItems
    ?.map((item) => item.productId)
    ?.filter(Boolean);
  const productNames = lineItems?.map((item) => item.name)?.filter(Boolean);

  if (!bigCommerceIds || bigCommerceIds.length === 0) {
    return { url: "", queryStringParams: {} };
  }

  return {
    url: API_URL,
    queryStringParams: {
      bigCommerceIds: JSON.stringify(bigCommerceIds),
      productNames: JSON.stringify(productNames),
      customerGroup: JSON.stringify(customerGroup),
    },
  };
};
